import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type { ExternalTrainingSubmit } from '@gokrew/ui/components';
import { getConfig } from '@gokrew/ui/runtime-config';
import {
  TrainingAssignmentStatus,
  type TrainingQuizAttemptPayload,
} from '@gokrew/models/training';
import {
  downloadMyAttestation,
  getExternalTrainingFileUrl,
  getMemberExternalTrainings,
  getMemberTrainings,
  getMyExternalTrainings,
  getMyTraining,
  getMyTrainings,
  getMyTrainingSummary,
  getTeamTrainings,
  removeExternalTrainingFile,
  submitExternalTraining,
  submitTrainingQuiz,
  updateExternalTraining,
  uploadExternalTrainingFile,
  withdrawExternalTraining,
} from '@/api/trainings';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';

export function useMyTrainings(enabled = true) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'list'],
    queryFn: getMyTrainings,
    enabled: !!user && enabled,
  });
}

/**
 * Open trainings for My Tasks and the dashboard (D108: read from the
 * assignments, never written as tasks), behind the build flag and the
 * company's Training module. Empty while either is off.
 */
export function usePendingTrainings() {
  const flag = !!getConfig().ENABLE_TRAINING;
  const summaryQuery = useMyTrainingSummary(flag);
  const summary = summaryQuery.data;
  const on = flag && !!summary?.enabled;
  const listQuery = useMyTrainings(on);
  const data = listQuery.data;
  return {
    enabled: on,
    isLoading: (flag && summaryQuery.isLoading) || (on && listQuery.isLoading),
    isError: (flag && summaryQuery.isError) || (on && listQuery.isError),
    refetch: async () => {
      if (!flag) return;
      await summaryQuery.refetch();
      if (on) await listQuery.refetch();
    },
    summary: on ? summary : undefined,
    // Failed ones wait on HR (another attempt), not on the employee.
    items: on
      ? (data?.todo ?? []).filter(
          (item) =>
            item.status === TrainingAssignmentStatus.NotStarted ||
            item.status === TrainingAssignmentStatus.InProgress
        )
      : [],
  };
}

/** The nav badge: open trainings, refreshed every minute. */
export function useMyTrainingSummary(enabled = true) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'summary'],
    queryFn: getMyTrainingSummary,
    enabled: !!user && enabled,
    refetchInterval: 60_000,
  });
}

export function useMyTraining(id?: string) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'one', id],
    queryFn: () => getMyTraining(id!),
    enabled: !!user && !!id,
    // Signed media URLs last an hour.
    staleTime: 30 * 60 * 1000,
  });
}

/** Refreshes the training, the list and the badge after progress. */
export function useRefreshMyTrainings() {
  const { user } = useEmployeePortalAuth();
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.MyTrainings, user?.id],
    });
}

export function useSubmitTrainingQuiz(id: string) {
  return useMutation({
    mutationFn: ({
      quizKey,
      payload,
    }: {
      quizKey: string;
      payload: TrainingQuizAttemptPayload;
    }) => submitTrainingQuiz(id, quizKey, payload),
  });
}

/** Saves my certificate of one completed training. */
export function useDownloadMyAttestation() {
  return useMutation({
    mutationFn: downloadMyAttestation,
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    },
  });
}

/** Manager: whether training views apply (build flag and company module). */
export function useTeamTrainingsAvailable() {
  const flag = !!getConfig().ENABLE_TRAINING;
  const { data: summary } = useMyTrainingSummary(flag);
  return flag && !!summary?.enabled;
}

/** Manager: My Team > Trainings (subtree, no answers). */
export function useTeamTrainings(enabled: boolean) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'team'],
    queryFn: getTeamTrainings,
    enabled: !!user && enabled,
  });
}

/** Manager: one member's trainings on their page. */
export function useMemberTrainings(employeeId: string, enabled: boolean) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'member', employeeId],
    queryFn: () => getMemberTrainings(employeeId),
    enabled: !!user && enabled,
  });
}

/** External trainings and the spend ledger ship behind their own flag. */
export const useTrainingSpendEnabled = () =>
  !!getConfig().ENABLE_TRAINING && !!getConfig().ENABLE_TRAINING_SPEND;

/** My trainings > External trainings (D123). */
export function useMyExternalTrainings(enabled: boolean) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'external'],
    queryFn: getMyExternalTrainings,
    enabled: !!user && enabled,
  });
}

/** Submit, edit, withdraw, and open a file of my own entries. */
export function useMyExternalTrainingActions() {
  const { user } = useEmployeePortalAuth();
  const { t } = useTranslation(TranslationNS.Training);
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.MyTrainings, user?.id, 'external'],
    });

  const save = useMutation({
    mutationFn: async ({
      id,
      submit,
    }: {
      id?: string;
      submit: ExternalTrainingSubmit;
    }) => {
      const entry = id
        ? await updateExternalTraining(id, submit.payload)
        : await submitExternalTraining(submit.payload);
      for (const fileId of submit.removedFileIds) {
        await removeExternalTrainingFile(entry.id, fileId);
      }
      for (const { file, kind } of submit.newFiles) {
        await uploadExternalTrainingFile(entry.id, file, kind);
      }
      return entry;
    },
    onSuccess: async (_, { id }) => {
      await refresh();
      toast.success(
        t(id ? 'external.toast.saved' : 'external.toast.submitted')
      );
    },
  });

  const withdraw = useMutation({
    mutationFn: withdrawExternalTraining,
    onSuccess: async () => {
      await refresh();
      toast.success(t('external.toast.withdrawn'));
    },
  });

  const openFile = useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) =>
      getExternalTrainingFileUrl(id, fileId),
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });

  return { save, withdraw, openFile };
}

/** Manager: one member's approved external trainings, no costs. */
export function useMemberExternalTrainings(
  employeeId: string,
  enabled: boolean
) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTrainings, user?.id, 'member-external', employeeId],
    queryFn: () => getMemberExternalTrainings(employeeId),
    enabled: !!user && enabled,
  });
}
